import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { FiLogOut, FiBell, FiSearch } from 'react-icons/fi';

// Humanizes the active route into a breadcrumb + page title, matching the
// redesign's header pattern (".crumb" + "h1") without needing every page to
// supply its own title via context/props — derived straight from the URL.
// "/diesel" or "/diesel/" -> crumb "Diesel Portal", title "Dashboard".
// "/diesel/technician-integrity" -> crumb "Diesel Portal", title "Technician Integrity".
function usePageTitle(): { crumb: string; title: string } {
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean);
  const portal = segments[0] || '';
  const crumb = portal
    ? `${portal.charAt(0).toUpperCase()}${portal.slice(1).replace(/-/g, ' ')} Portal`
    : 'Portal';
  const last = segments[segments.length - 1];
  const title =
    !last || last === portal
      ? 'Dashboard'
      : last
          .replace(/-/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());
  return { crumb, title };
}

function initialsOf(fullName?: string): string {
  if (!fullName) return '?';
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase() || '?';
}

export const Header: React.FC = () => {
  const { user, logout } = useAuthContext();
  const navigate = useNavigate();
  const { crumb, title } = usePageTitle();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header
      className="bg-white flex items-center gap-4"
      style={{ height: 60, borderBottom: '1px solid #e5e7eb', padding: '0 18px' }}
    >
      <div className="min-w-0">
        <div className="truncate" style={{ fontSize: 12, color: '#9a9ca5' }}>{crumb}</div>
        <h1 className="truncate" style={{ fontSize: 17, fontWeight: 700, color: '#1f2024' }}>{title}</h1>
      </div>

      <div
        className="hidden md:flex items-center gap-2"
        style={{
          flex: '1 1 auto', maxWidth: 360, background: '#fafafa', border: '1px solid #e5e7eb',
          borderRadius: 999, padding: '7px 12px', color: '#9a9ca5', marginLeft: 10,
        }}
      >
        <FiSearch className="flex-shrink-0" style={{ width: 15, height: 15 }} />
        <input
          type="text"
          placeholder="Search sites, requests, cycles…"
          aria-label="Search"
          style={{ border: 'none', background: 'none', outline: 'none', fontSize: 13, color: '#1f2024', width: '100%' }}
        />
      </div>

      <div className="flex items-center gap-2.5" style={{ marginLeft: 'auto', flex: '0 0 auto' }}>
        <button
          aria-label="Notifications"
          className="flex items-center justify-center"
          style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#6b6d76' }}
        >
          <FiBell style={{ width: 18, height: 18 }} />
        </button>

        <div className="flex items-center gap-2" style={{ paddingLeft: 8, borderLeft: '1px solid #e5e7eb' }}>
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{
              width: 32, height: 32, borderRadius: '50%', background: '#fef2f2', color: '#c62828',
              fontWeight: 700, fontSize: 13,
            }}
          >
            {initialsOf(user?.fullName)}
          </div>
          <div className="hidden sm:block" style={{ fontSize: 13 }}>
            <p className="font-semibold leading-tight" style={{ color: '#1f2024' }}>{user?.fullName}</p>
            <p className="capitalize leading-tight" style={{ color: '#9a9ca5', fontSize: 11 }}>{user?.role.replace('_', ' ')}</p>
          </div>
          <button
            onClick={handleLogout}
            aria-label="Log out"
            className="flex items-center justify-center"
            style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#6b6d76', marginLeft: 4 }}
          >
            <FiLogOut style={{ width: 16, height: 16 }} />
          </button>
        </div>
      </div>
    </header>
  );
};
